let html2canvasPromise: Promise<typeof import('html2canvas').default> | null = null

const loadHtml2Canvas = async () => {
  if (!html2canvasPromise) {
    html2canvasPromise = import('html2canvas').then((mod) => mod.default)
  }
  return html2canvasPromise
}

const maskSensitiveInputs = (): (() => void) => {
  if (typeof document === 'undefined') return () => undefined
  const restorers: Array<() => void> = []
  document.querySelectorAll<HTMLInputElement>('input[type="password"], input[data-debug-mask="true"]').forEach((input) => {
    const original = input.value
    input.value = original.length ? '•'.repeat(original.length) : ''
    restorers.push(() => {
      input.value = original
    })
  })
  return () => restorers.forEach((restore) => restore())
}

export type DebugScreenshotCapture = {
  blob: Blob
  width: number
  height: number
  capturedAt: string
  mimeType: string
}

export const captureDebugScreenshot = async (options: {
  scale?: number
  quality?: number
  maxBytes?: number
} = {}): Promise<DebugScreenshotCapture | null> => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null
  const html2canvas = await loadHtml2Canvas().catch(() => null)
  if (!html2canvas) return null

  const scale = options.scale ?? Math.min(window.devicePixelRatio || 1, 1.25)
  const quality = options.quality ?? 0.7
  const maxBytes = options.maxBytes ?? 900_000

  const restoreMasks = maskSensitiveInputs()
  let canvas: HTMLCanvasElement
  try {
    canvas = await html2canvas(document.body, {
      backgroundColor: null,
      logging: false,
      scale,
      useCORS: true,
      allowTaint: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      ignoreElements: (element) => {
        if (!(element instanceof HTMLElement)) return false
        return element.dataset.debugIgnore === 'true' || element.classList.contains('debugPopover')
      },
    })
  } catch {
    restoreMasks()
    return null
  }
  restoreMasks()

  let currentQuality = quality
  let blob: Blob | null = null
  for (let attempt = 0; attempt < 3; attempt += 1) {
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/jpeg', currentQuality)
    })
    if (!blob) return null
    if (blob.size <= maxBytes) break
    currentQuality = Math.max(0.3, currentQuality - 0.15)
  }
  if (!blob) return null

  return {
    blob,
    width: canvas.width,
    height: canvas.height,
    capturedAt: new Date().toISOString(),
    mimeType: 'image/jpeg',
  }
}
